import { useState, useCallback } from 'react';
import {
    fetchAnalyticsFromDb,
    type AnalyticsParams,
    type AnalyticsResponse,
} from '../services/analyticsService';
import {getErrorMessage} from "../utils/errors.ts";

export function useAnalytics() {
    // `data` holds the last successful analytics result, or null before the first fetch.
    const [data,    setData]    = useState<AnalyticsResponse | null>(null);
    // `loading` is true while a fetch is in flight — use this to show a spinner.
    const [loading, setLoading] = useState(false);
    // `error` holds a human-readable message if the last fetch failed, otherwise null.
    const [error,   setError]   = useState<string | null>(null);

    // Wrapped in useCallback so the function reference is stable across re-renders.
    // This prevents unnecessary re-renders in components that receive it as a prop
    // or list it as an effect dependency.
    const fetchAnalytics = useCallback(async (
        params: AnalyticsParams
    ): Promise<AnalyticsResponse | null> => {
        setLoading(true);
        setError(null); // Clear any previous error before starting a new request.
        try {
            const result = await fetchAnalyticsFromDb(params);
            setData(result);
            return result; // Also returned directly so callers can act on the result immediately.
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to load analytics'));
            return null;
        } finally {
            // Always clear the loading state, whether the fetch succeeded or failed.
            setLoading(false);
        }
    }, []);

    return {
        data,       // The aggregated analytics result for the requested period.
        loading,    // True while the fetch is in progress.
        error,      // Non-null if the last fetch threw an error.
        fetchAnalytics, // Call this with AnalyticsParams to trigger a new fetch.
    };
}